import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * 파일 드래그 앤 드롭 관련 상태 및 로직을 관리하는 커스텀 훅
 *
 * @param {Object} options
 * @param {Function} options.onFileDrop - 파일 드롭 시 호출되는 콜백
 * @param {string[]} options.acceptedTypes - 허용되는 MIME 타입 (예: ['image/png', 'image/jpeg'])
 * @param {number} options.maxSize - 최대 파일 크기 (bytes)
 * @param {boolean} options.multiple - 여러 파일 허용 여부
 */
const useFileDrop = ({
    onFileDrop,
    acceptedTypes = ['image/png', 'image/jpeg'],
    maxSize = 10 * 1024 * 1024, // 10MB
    multiple = false,
} = {}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState(null);
    const dragCounterRef = useRef(0);

    // 에러 자동 클리어
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    // 파일 유효성 검사
    const validateFile = useCallback((file) => {
        if (!acceptedTypes.includes(file.type)) {
            return `지원하지 않는 파일 형식입니다. (${acceptedTypes.join(', ')})`;
        }
        if (file.size > maxSize) {
            return `파일 크기는 ${Math.round(maxSize / 1024 / 1024)}MB 이하여야 합니다.`;
        }
        return null;
    }, [acceptedTypes, maxSize]);

    // 드래그 진입
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current++;

        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    }, []);

    // 드래그 이탈
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current--;

        if (dragCounterRef.current === 0) {
            setIsDragging(false);
        }
    }, []);

    // 드래그 오버 (드롭 허용을 위해 필요)
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    // 드롭
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragging(false);
        dragCounterRef.current = 0;

        const files = Array.from(e.dataTransfer.files);

        if (files.length === 0) {
            return;
        }

        // 단일 파일만 허용하는 경우
        const filesToProcess = multiple ? files : [files[0]];

        // 파일 유효성 검사
        for (const file of filesToProcess) {
            const validationError = validateFile(file);
            if (validationError) {
                setError(validationError);
                return;
            }
        }

        // 콜백 호출
        if (onFileDrop) {
            if (multiple) {
                onFileDrop(filesToProcess);
            } else {
                onFileDrop(filesToProcess[0]);
            }
        }
    }, [multiple, validateFile, onFileDrop]);

    // ref에 이벤트 바인딩하는 함수
    const getDropZoneProps = useCallback(() => ({
        onDragEnter: handleDragEnter,
        onDragLeave: handleDragLeave,
        onDragOver: handleDragOver,
        onDrop: handleDrop,
    }), [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

    return {
        isDragging,
        error,
        setError,
        getDropZoneProps,
        // 개별 핸들러 (필요한 경우)
        handleDragEnter,
        handleDragLeave,
        handleDragOver,
        handleDrop,
    };
};

export default useFileDrop;
